import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { Button } from "@/components/Button";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength } from "@/components/PasswordStrength";
import { ApiError, memberApi } from "@/lib/api";
import type { MemberRegisterResponse } from "@/types/api";

const schema = z.object({
  full_name: z.string().min(2, "Bitte vollständigen Namen eintragen"),
  email: z.string().email("Gültige Email erforderlich"),
  phone: z
    .string()
    .max(64, "Max. 64 Zeichen")
    .optional()
    .or(z.literal(""))
    .transform((v) => (v && v.trim().length > 0 ? v.trim() : undefined)),
  password: z.string().min(8, "Mindestens 8 Zeichen"),
  marketing_opt_in: z.boolean().default(false),
  accept_terms: z.literal(true, {
    errorMap: () => ({ message: "Bitte AGB und Datenschutz akzeptieren" }),
  }),
});
type FormValues = {
  full_name: string;
  email: string;
  phone: string;
  password: string;
  marketing_opt_in: boolean;
  accept_terms: true;
};

const labelCls = "block text-body-sm font-medium text-ink";
const fieldCls =
  "mt-1 block w-full rounded border border-line bg-surface px-3 py-2 text-body-sm text-ink shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/30 focus:outline-none";
const errCls = "mt-1 text-caption text-danger";

function Req() {
  return (
    <span className="ml-0.5 text-danger" aria-hidden>
      *
    </span>
  );
}

export function RegisterPage() {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      full_name: "",
      email: "",
      phone: "",
      password: "",
      marketing_opt_in: false,
      accept_terms: undefined as unknown as true,
    },
  });

  const passwordValue = watch("password");

  const mutation = useMutation<MemberRegisterResponse, ApiError, FormValues>({
    mutationFn: (values) => {
      const parsed = schema.parse(values);
      return memberApi.register({
        full_name: parsed.full_name,
        email: parsed.email,
        phone: parsed.phone,
        password: parsed.password,
        marketing_opt_in: parsed.marketing_opt_in ?? false,
        accept_terms: parsed.accept_terms,
      });
    },
  });

  if (mutation.isSuccess) {
    const submittedEmail = watch("email");
    return (
      <div className="mx-auto max-w-xl px-6 py-12">
        <p className="mb-3 text-caption font-medium uppercase tracking-widest text-primary">
          Fast geschafft
        </p>
        <h1 className="mb-3 font-display text-h1 uppercase text-primary">
          Bitte Email bestätigen
        </h1>
        <p className="mb-6 text-lead text-ink-soft">
          Wir haben dir gerade einen Bestätigungs-Link an{" "}
          <span className="font-medium text-ink">{submittedEmail}</span>{" "}
          geschickt. Bitte klick auf den Link in der Email — der Link ist 72&nbsp;Stunden gültig.
        </p>

        <ol className="mb-6 space-y-3 rounded-card border border-line bg-surface p-5">
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-caption font-bold text-white">
              1
            </span>
            <span className="text-body-sm text-ink">
              <strong>Email bestätigen</strong> — Link in der Bestätigungs-Mail anklicken.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-caption font-bold text-white">
              2
            </span>
            <span className="text-body-sm text-ink">
              <strong>Charlie schaltet dich frei</strong> — erst nach der Email-Bestätigung
              wird deine Anmeldung an die Tanzraum-Inhaberin weitergeleitet und persönlich
              geprüft. Vorher passiert nichts.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-caption font-bold text-white">
              3
            </span>
            <span className="text-body-sm text-ink">
              <strong>Freischaltungs-Email</strong> — sobald Charlie dein Konto freigegeben
              hat, bekommst du eine zweite Email. Erst dann kannst du dich einloggen und
              buchen.
            </span>
          </li>
        </ol>

        <p className="mb-6 text-caption text-ink-muted">
          Keine Email im Posteingang? Bitte schau im <strong>Spam-Ordner</strong> nach.
          Falls die Mail dort liegt, hilft es, sie als „Kein Spam" zu markieren.
        </p>

        <Link to="/konto/login" className="text-primary underline">
          Zur Anmeldung
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-6 py-12">
      <h1 className="mb-2 font-display text-h1 uppercase text-primary">Konto anlegen</h1>
      <p className="mb-4 text-body text-ink-soft">
        Ein Konto gibt es ausschließlich für ausgewählte Tanzlehrer:innen, die mit der
        Tanzschule Tanzraum assoziiert sind. Charlie prüft jede Anmeldung persönlich.
        Mit deinem Konto siehst du alle Buchungen auf einen Blick und kannst Termine direkt
        in Google Kalender, Apple Kalender oder Outlook importieren.
      </p>
      <p className="mb-8 text-caption text-ink-muted">
        Felder mit <span className="text-danger">*</span> sind Pflicht.
      </p>

      <form
        onSubmit={handleSubmit((v) => mutation.mutate(v))}
        className="space-y-5"
        noValidate
      >
        <div>
          <label htmlFor="full_name" className={labelCls}>
            Vollständiger Name<Req />
          </label>
          <input
            id="full_name"
            autoComplete="name"
            aria-required="true"
            {...register("full_name")}
            className={fieldCls}
          />
          {errors.full_name && <p className={errCls}>{errors.full_name.message}</p>}
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="email" className={labelCls}>
              Email<Req />
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              aria-required="true"
              {...register("email")}
              className={fieldCls}
            />
            {errors.email && <p className={errCls}>{errors.email.message}</p>}
          </div>
          <div>
            <label htmlFor="phone" className={labelCls}>
              Telefon <span className="text-ink-muted">(optional)</span>
            </label>
            <input
              id="phone"
              type="tel"
              autoComplete="tel"
              {...register("phone")}
              className={fieldCls}
            />
            {errors.phone && <p className={errCls}>{errors.phone.message}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="password" className={labelCls}>
            Passwort<Req />
          </label>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            aria-required="true"
            invalid={!!errors.password}
            {...register("password")}
            className="mt-1"
          />
          {errors.password && <p className={errCls}>{errors.password.message}</p>}
          <p className="mt-1 text-caption text-ink-muted">
            Mindestens 8 Zeichen. Tipp: Klein- und Großbuchstaben, Zahl und Sonderzeichen für mehr Sicherheit.
          </p>
          <PasswordStrength value={passwordValue ?? ""} />
        </div>

        <fieldset className="space-y-3 rounded-card border border-line bg-surface p-4">
          <label className="flex items-start gap-3 text-body-sm text-ink">
            <input
              type="checkbox"
              {...register("accept_terms")}
              className="mt-1 h-4 w-4 rounded border-line text-primary focus:ring-primary"
            />
            <span>
              Ich akzeptiere{" "}
              <a href="/agb" target="_blank" rel="noreferrer" className="text-primary underline">
                AGB
              </a>{" "}
              und{" "}
              <a
                href="/datenschutz"
                target="_blank"
                rel="noreferrer"
                className="text-primary underline"
              >
                Datenschutz
              </a>
              .<Req />
            </span>
          </label>
          {errors.accept_terms && <p className={errCls}>{errors.accept_terms.message}</p>}

          <label className="flex items-start gap-3 text-body-sm text-ink-soft">
            <input
              type="checkbox"
              {...register("marketing_opt_in")}
              className="mt-1 h-4 w-4 rounded border-line text-primary focus:ring-primary"
            />
            <span>
              Ich möchte gelegentlich Infos zu neuen Slots und Tanzraum-Angeboten erhalten.
              Jederzeit abbestellbar. <span className="text-ink-muted">(optional)</span>
            </span>
          </label>
        </fieldset>

        {mutation.error && (
          <div className="rounded-card border border-danger bg-danger/5 p-3 text-body-sm text-danger">
            {mutation.error.message}
          </div>
        )}

        <Button type="submit" size="lg" loading={isSubmitting || mutation.isPending}>
          Konto anlegen
        </Button>

        <p className="text-caption text-ink-muted">
          Schon ein Konto?{" "}
          <Link to="/konto/login" className="text-primary underline">
            Hier einloggen
          </Link>
          .
        </p>
      </form>
    </div>
  );
}
